import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import ListAltIcon from "@mui/icons-material/ListAlt";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ShareIcon from "@mui/icons-material/Share";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import RestoreIcon from "@mui/icons-material/Restore";
import OpenWithIcon from "@mui/icons-material/OpenWith";
import useChatbot from "./useChatbot";
import { HomeAppContext } from "../Home";

const sizeStorageKey = "aiChatWidget.size";
const stateStorageKey = "aiChatWidget.state";

const defaultCompactSize = { width: 440, height: 560 };
const defaultExpandedSize = { width: 760, height: 720 };
const minWidth = 360;
const minHeight = 320;

const shareTypeLabels = {
  0: "Private",
  1: "Read-only",
  2: "Public",
};

const badgeColors = ["default", "warning", "success"];

const MessageBubble = ({ message, isUser }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
        mb: 1.2,
      }}
    >
      <Box
        sx={{
          maxWidth: "80%",
          p: 1.5,
          borderRadius: 2,
          bgcolor: isUser ? theme.palette.primary.main : theme.palette.background.paper,
          color: isUser ? "#fff" : theme.palette.text.primary,
          boxShadow: "0px 2px 6px rgba(0,0,0,0.14)",
          whiteSpace: "pre-wrap",
        }}
      >
        {message.text}
      </Box>
    </Box>
  );
};

const StatusDot = ({ status }) => {
  const theme = useTheme();
  const color =
    status === "connected"
      ? theme.palette.success.main
      : status === "connecting"
      ? theme.palette.warning.main
      : theme.palette.error.main;
  return (
    <Box
      sx={{
        width: 10,
        height: 10,
        borderRadius: "50%",
        bgcolor: color,
      }}
    />
  );
};

const ChatWidget = () => {
  const theme = useTheme();
  const { homeState } = useContext(HomeAppContext);
  const {
    conversations,
    selectedConversation,
    selectedConversationId,
    messages,
    loading,
    historyLoading,
    aiEngines,
    groupedEngines,
    engineFamilies,
    selectedEngineFamily,
    selectedAiEngine,
    activeTab,
    connectionStatus,
    isReadOnly,
    setActiveTab,
    handleEngineFamilyChange,
    handleAiEngineChange,
    handleSelectConversation,
    handleNewConversation,
    handleSaveConversationTitle,
    handleDeleteConversation,
    handleShareConversation,
    sendMessage,
  } = useChatbot();

  const [isOpen, setIsOpen] = useState(() => {
    if (typeof window === "undefined") return false;
    const stored = window.localStorage.getItem(stateStorageKey);
    if (!stored) return false;
    try {
      const parsed = JSON.parse(stored);
      return !!parsed.isOpen;
    } catch {
      return false;
    }
  });
  const [isExpanded, setIsExpanded] = useState(() => {
    if (typeof window === "undefined") return false;
    const stored = window.localStorage.getItem(stateStorageKey);
    if (!stored) return false;
    try {
      const parsed = JSON.parse(stored);
      return !!parsed.isExpanded;
    } catch {
      return false;
    }
  });
  const [size, setSize] = useState(() => {
    if (typeof window === "undefined") return defaultCompactSize;
    const stored = window.localStorage.getItem(sizeStorageKey);
    if (!stored) return defaultCompactSize;
    try {
      const parsed = JSON.parse(stored);
      return parsed;
    } catch {
      return defaultCompactSize;
    }
  });
  const [historyVisible, setHistoryVisible] = useState(true);
  const [draftMessage, setDraftMessage] = useState("");
  const [editingConversationId, setEditingConversationId] = useState(null);
  const [editedTitle, setEditedTitle] = useState("");
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareConversationId, setShareConversationId] = useState(null);
  const [shareType, setShareType] = useState(0);
  const [isResizing, setIsResizing] = useState(false);

  const widgetRef = useRef(null);
  const messageEndRef = useRef(null);
  const resizeOriginRef = useRef({
    startX: 0,
    startY: 0,
    startWidth: defaultCompactSize.width,
    startHeight: defaultCompactSize.height,
  });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    window.localStorage.setItem(
      stateStorageKey,
      JSON.stringify({ isOpen, isExpanded })
    );
    return undefined;
  }, [isOpen, isExpanded]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    window.localStorage.setItem(sizeStorageKey, JSON.stringify(size));
    return undefined;
  }, [size]);

  useEffect(() => {
    if (!widgetRef.current) return undefined;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry?.contentRect) {
        setSize({
          width: Math.round(entry.contentRect.width),
          height: Math.round(entry.contentRect.height),
        });
      }
    });
    observer.observe(widgetRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, selectedConversationId]);

  useEffect(() => {
    if (!isResizing) return undefined;

    const handlePointerMove = (event) => {
      const point = event.touches ? event.touches[0] : event;
      if (!point) return;
      event.preventDefault();
      const deltaX = point.clientX - resizeOriginRef.current.startX;
      const deltaY = point.clientY - resizeOriginRef.current.startY;
      setSize((prev) => {
        const nextWidth = Math.max(minWidth, resizeOriginRef.current.startWidth - deltaX);
        const nextHeight = Math.max(minHeight, resizeOriginRef.current.startHeight - deltaY);
        if (prev.width === nextWidth && prev.height === nextHeight) return prev;
        return { width: nextWidth, height: nextHeight };
      });
    };

    const handlePointerUp = () => {
      setIsResizing(false);
    };

    window.addEventListener("mousemove", handlePointerMove, { passive: false });
    window.addEventListener("mouseup", handlePointerUp);
    window.addEventListener("touchmove", handlePointerMove, { passive: false });
    window.addEventListener("touchend", handlePointerUp);
    window.addEventListener("touchcancel", handlePointerUp);

    return () => {
      window.removeEventListener("mousemove", handlePointerMove);
      window.removeEventListener("mouseup", handlePointerUp);
      window.removeEventListener("touchmove", handlePointerMove);
      window.removeEventListener("touchend", handlePointerUp);
      window.removeEventListener("touchcancel", handlePointerUp);
    };
  }, [isResizing]);

  useEffect(() => {
    if (!isResizing) return undefined;
    const previousCursor = document.body.style.cursor;
    document.body.style.cursor = "nwse-resize";
    return () => {
      document.body.style.cursor = previousCursor;
    };
  }, [isResizing]);

  const handleToggleOpen = () => setIsOpen((prev) => !prev);

  const handleToggleExpand = () => {
    setIsExpanded((prev) => {
      if (!prev && widgetRef.current) {
        setSize(defaultExpandedSize);
      } else if (prev && widgetRef.current) {
        setSize(defaultCompactSize);
      }
      return !prev;
    });
  };

  const handleSendMessage = async () => {
    if (!draftMessage.trim()) return;
    try {
      await sendMessage(draftMessage);
      setDraftMessage("");
    } catch (error) {
      console.error("Unable to send message from widget", error);
    }
  };

  const openShareDialog = (id, currentShareType) => {
    setShareConversationId(id);
    setShareType(currentShareType);
    setShareDialogOpen(true);
  };

  const confirmShareDialog = async () => {
    if (shareConversationId) {
      await handleShareConversation(shareConversationId, shareType);
    }
    setShareDialogOpen(false);
    setShareConversationId(null);
  };

  const stopEditing = () => {
    setEditingConversationId(null);
    setEditedTitle("");
  };

  const activeShareChip = useMemo(() => {
    if (!selectedConversation) return null;
    const color = badgeColors[selectedConversation.shareType] || "default";
    return (
      <Chip
        size="small"
        label={shareTypeLabels[selectedConversation.shareType] || "Private"}
        color={color}
        variant="outlined"
        sx={{ ml: 1 }}
      />
    );
  }, [selectedConversation]);

  const handleResizeStart = (event) => {
    const point = event.touches ? event.touches[0] : event;
    if (!point || !widgetRef.current) return;
    resizeOriginRef.current.startX = point.clientX;
    resizeOriginRef.current.startY = point.clientY;
    resizeOriginRef.current.startWidth = widgetRef.current.getBoundingClientRect().width;
    resizeOriginRef.current.startHeight = widgetRef.current.getBoundingClientRect().height;
    setIsResizing(true);
    event.preventDefault();
  };

  if (!isOpen) {
    return (
      <Box
        sx={{
          position: "fixed",
          right: 130,
          bottom: 50,
          zIndex: theme.zIndex.modal - 10,
        }}
      >
        <Tooltip title="Chat with AI">
          <IconButton
            color="primary"
            size="large"
            sx={{
              bgcolor: theme.palette.background.paper,
              boxShadow: "0px 8px 18px rgba(0,0,0,0)",
              border: `1px solid ${theme.palette.divider}`,
              width: 100,
              height: 100,
              "& svg": { fontSize: "3rem" },
            }}
            onClick={() => setIsOpen(true)}
          >
            <SmartToyIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  const showHistory = historyVisible && isExpanded && size.width > 620;
  const gridTemplate = showHistory ? "320px 1fr" : "1fr";
  const sendDisabled = loading || isReadOnly || !draftMessage.trim() || !selectedAiEngine;
  const sendTooltip = isReadOnly
    ? "Read-only conversation"
    : !selectedAiEngine
    ? "Select an AI engine to start"
    : "Send";
  const historyToggleDisabled = !isExpanded;
  const historyTooltip = historyToggleDisabled
    ? "Expand to manage history"
    : historyVisible
    ? "Hide history"
    : "Show history";

  return (
    <Paper
      ref={widgetRef}
      elevation={6}
      sx={{
        position: "fixed",
        right: 16,
        bottom: 16,
        width: size.width,
        height: size.height,
        minWidth,
        minHeight,
        maxWidth: "95vw",
        maxHeight: "92vh",
        display: "flex",
        flexDirection: "column",
        borderRadius: 2.5,
        overflow: "hidden",
        zIndex: theme.zIndex.modal - 10,
        backdropFilter: "blur(10px)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.mode === "dark" ? "rgba(0,0,0,0.4)" : "rgba(255,255,255,0.8)",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <IconButton
              size="medium"
              onMouseDown={handleResizeStart}
              onTouchStart={handleResizeStart}
              sx={{ cursor: "nwse-resize", width: 38, height: 38 }}
            >
              <OpenWithIcon fontSize="medium" />
            </IconButton>
            <Typography variant="caption" color="text.secondary">
              Drag
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SmartToyIcon color="primary" />
            <Box>
              <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                AI Copilot
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <StatusDot status={connectionStatus} />
                <Typography variant="caption" color="text.secondary">
                  {connectionStatus === "connected"
                    ? "Live"
                    : connectionStatus === "connecting"
                    ? "Connecting..."
                    : "Offline"}
                </Typography>
              </Box>
            </Box>
            {activeShareChip}
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Tooltip title={historyTooltip}>
            <span>
              <IconButton
                onClick={() => setHistoryVisible((prev) => !prev)}
                size="small"
                disabled={historyToggleDisabled}
              >
                <ListAltIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={isExpanded ? "Compact" : "Expand"}>
            <IconButton onClick={handleToggleExpand} size="small">
              {isExpanded ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Minimize">
            <IconButton onClick={handleToggleOpen} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: gridTemplate,
          gap: 1,
          p: 1.5,
          overflow: "hidden",
        }}
      >
        {showHistory && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <Box sx={{ display: "flex", gap: 1 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Engine Family</InputLabel>
                <Select
                  label="Engine Family"
                  value={selectedEngineFamily}
                  onChange={(e) => handleEngineFamilyChange(e.target.value)}
                >
                  {engineFamilies.map((family) => (
                    <MenuItem key={family} value={family}>
                      {family}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth disabled={!selectedEngineFamily}>
                <InputLabel>Engine</InputLabel>
                <Select
                  label="Engine"
                  value={selectedAiEngine}
                  onChange={(e) => handleAiEngineChange(e.target.value)}
                >
                  {selectedEngineFamily &&
                    groupedEngines[selectedEngineFamily]?.map((engine) => (
                      <MenuItem key={engine.id} value={engine.id}>
                        {engine.engineName}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>

            <Tabs
              value={activeTab}
              onChange={(_, val) => setActiveTab(val)}
              variant="fullWidth"
            >
              <Tab label="All" />
              <Tab label="Mine" />
              <Tab label="Shared" />
            </Tabs>

            <Paper
              variant="outlined"
              sx={{
                flex: 1,
                overflowY: "auto",
                p: 0.5,
              }}
            >
              <List dense>
                {historyLoading ? (
                  <ListItem>
                    <ListItemText primary="Loading conversations..." />
                  </ListItem>
                ) : (
                  conversations.map((conv) => {
                    const isSelected = conv.id === selectedConversationId;
                    const canManage = conv.userId === homeState.userProfile?.userId;
                    const canEdit =
                      canManage ||
                      (conv.userId !== homeState.userProfile?.userId && conv.shareType === 2);
                    const shareColor = badgeColors[conv.shareType] || "default";
                    return (
                      <ListItem
                        key={conv.id}
                        selected={isSelected}
                        onClick={() => handleSelectConversation(conv.id)}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          "&.Mui-selected": {
                            bgcolor: theme.palette.primary.main,
                            color: theme.palette.getContrastText(theme.palette.primary.main),
                          },
                        }}
                        secondaryAction={
                          (canManage || canEdit) && (
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                              {canManage && (
                                <Tooltip title="Share">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openShareDialog(conv.id, conv.shareType);
                                    }}
                                    color="inherit"
                                  >
                                    <ShareIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {canEdit && (
                                <Tooltip title="Rename">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingConversationId(conv.id);
                                      setEditedTitle(conv.title);
                                    }}
                                    color="inherit"
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {canManage && (
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const confirmed = window.confirm(
                                      "Delete this conversation? This cannot be undone."
                                    );
                                    if (confirmed) {
                                      handleDeleteConversation(conv.id);
                                    }
                                  }}
                                  color="inherit"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          )
                        }
                      >
                        {editingConversationId === conv.id ? (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%" }}>
                            <TextField
                              value={editedTitle}
                              onChange={(e) => setEditedTitle(e.target.value)}
                              size="small"
                              fullWidth
                            />
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSaveConversationTitle(conv.id, editedTitle);
                                stopEditing();
                              }}
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                stopEditing();
                              }}
                            >
                              <RestoreIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <ListItemText
                            primary={conv.title || `Conversation ${conv.id}`}
                            secondary={
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  color={shareColor}
                                  label={shareTypeLabels[conv.shareType] || "Private"}
                                />
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {aiEngines.find((engine) => engine.id === conv.aiEngine)?.engineName ||
                                    "Engine"}
                                </Typography>
                              </Box>
                            }
                          />
                        )}
                      </ListItem>
                    );
                  })
                )}
              </List>
            </Paper>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleNewConversation}
            >
              New Conversation
            </Button>
          </Box>
        )}

        <Paper
          variant="outlined"
          sx={{
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: 1,
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary" noWrap>
              {selectedEngineFamily ? `${selectedEngineFamily}` : "Select an engine to start"}
            </Typography>
            {selectedConversation?.title && (
              <Typography variant="subtitle2" noWrap>
                · {selectedConversation.title}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 1.5,
              background:
                theme.palette.mode === "dark"
                  ? "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0))"
                  : "linear-gradient(180deg, rgba(0,0,0,0.02), rgba(0,0,0,0))",
            }}
          >
            {messages.length === 0 ? (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  color: "text.secondary",
                }}
              >
                <Typography variant="body2">
                  Start a conversation by asking a question or selecting a previous thread.
                </Typography>
              </Box>
            ) : (
              messages.map((msg, idx) => (
                <MessageBubble
                  key={idx}
                  message={msg}
                  isUser={msg.role === "user" || msg.role === "User"}
                />
              ))
            )}
            <div ref={messageEndRef} />
          </Box>

          <Box
            sx={{
              p: 1,
              borderTop: `1px solid ${theme.palette.divider}`,
              display: "flex",
              gap: 1,
              alignItems: "flex-end",
            }}
          >
            <TextField
              value={draftMessage}
              onChange={(e) => setDraftMessage(e.target.value)}
              multiline
              minRows={2}
              maxRows={4}
              fullWidth
              placeholder={
                isReadOnly
                  ? "This conversation is read-only."
                  : selectedAiEngine
                  ? "Type a prompt and press Enter to send"
                  : "Select an AI engine to start chatting"
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              disabled={loading || isReadOnly || !selectedAiEngine}
            />
            <Tooltip title={sendTooltip}>
              <span>
                <IconButton
                  color="primary"
                  onClick={handleSendMessage}
                  disabled={sendDisabled}
                  sx={{ alignSelf: "center" }}
                >
                  {loading ? <CircularProgress size={20} /> : <SendIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Paper>
      </Box>

      <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)} fullWidth>
        <DialogTitle>Share Settings</DialogTitle>
        <DialogContent>
          <RadioGroup
            value={shareType}
            onChange={(e) => setShareType(Number(e.target.value))}
            aria-label="Sharing options"
          >
            <FormControlLabel value={0} control={<Radio />} label="Private" />
            <FormControlLabel value={1} control={<Radio />} label="Public Read-Only" />
            <FormControlLabel value={2} control={<Radio />} label="Public (editable)" />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmShareDialog} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ChatWidget;
